import React, { useCallback, useMemo } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  Avatar,
  Typography,
  InputAdornment,
  Skeleton,
  IconButton,
  Chip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nScenarioEditorNs } from '@/i18n/dictionaries/interfaces';
import { useQueries } from '@tanstack/react-query';
import { getAvatarUrlWithSas } from '@/app/utils/avatarUtils';
import type { PersonaDropdownProps, PersonaDropdownOption, AddNewPersonaOption } from './PersonaDropdown.types';
import type { ScenarioPersonaDto, PersonaUsage } from '@/api-gen/api.schemas';
import { getPersonaGetPersonaQueryOptions, useRolesGetRoles } from '@/api-gen/api';

export const PersonaDropdown: React.FC<PersonaDropdownProps> = ({
  scenarioPersonas,
  selectedPersona,
  usageType,
  label,
  helperText,
  placeholder,
  disabled = false,
  onPersonaChange,
  onAddNewPersona,
  onEditPersona,
  showEditButton = false,
  hasError = false,
  errorMessage,
  scenarioLanguage
}) => {
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);

  // Load full persona details for each scenario persona
  const personaQueries = useQueries({
    queries: scenarioPersonas.map(sp => ({
      ...getPersonaGetPersonaQueryOptions(sp.personaId, {
        query: {
          enabled: !!sp.personaId,
          staleTime: 5 * 60 * 1000, // 5 minutes
          cacheTime: 10 * 60 * 1000, // 10 minutes
          retry: false,
          refetchOnWindowFocus: false
        }
      })
    }))
  });

  // Check if any persona is still loading
  const isLoadingPersonas = useMemo(() => {
    return personaQueries.some(query => query.isLoading);
  }, [personaQueries]);

  // Merge scenario persona data with loaded persona details
  const enrichedPersonas = useMemo<ScenarioPersonaDto[]>(() => {
    return scenarioPersonas.map((sp, index) => {
      const query = personaQueries[index];
      const loadedPersona = query?.data?.data || query?.data;
      
      // If loading, return original persona (might have partial data)
      if (query?.isLoading) {
        return sp;
      }
      
      // If error or no data, use fallback with persona ID
      if (query?.isError || !loadedPersona) {
        return {
          ...sp,
          persona: sp.persona || {
            id: sp.personaId,
            name: null,
            description: null,
            avatarUrl: null,
            languageCode: null,
            ttsProvider: null,
            gender: null,
            voiceName: null,
            voiceId: null
          }
        };
      }
      
      // Extract persona from response if it's wrapped
      const persona = 'data' in loadedPersona ? loadedPersona.data : loadedPersona;
      
      return {
        ...sp,
        persona: {
          id: persona.id,
          name: persona.name || null,
          description: persona.description || null,
          avatarUrl: persona.avatarUrl || null,
          languageCode: persona.languageCode || null,
          ttsProvider: persona.ttsProvider || null,
          gender: persona.gender || null,
          voiceName: persona.voiceName || null,
          voiceId: persona.voiceId || null
        }
      };
    });
  }, [scenarioPersonas, personaQueries]);

  // Fetch roles to display role names for each scenario persona
  const { data: rolesResponse } = useRolesGetRoles(
    { langCode: scenarioLanguage || '' },
    { query: { enabled: !!scenarioLanguage, staleTime: 5 * 60 * 1000 } }
  );
  const roles = (rolesResponse as any)?.data || rolesResponse || [];
  const getRoleNameById = useCallback((id?: number | null): string | undefined => {
    if (!id) return undefined;
    const role = (roles as any[])?.find((r: any) => r.id === id);
    return role?.name as string | undefined;
  }, [roles]);

  // Helper functions for persona display
  const getInitials = useCallback((name?: string) => {
    if (!name) return 'P';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }, []);

  const getAvatarColor = useCallback((id?: number) => {
    const colors = [
      '#1976d2', '#388e3c', '#d32f2f', '#7b1fa2',
      '#c2185b', '#0288d1', '#0097a7', '#00796b'
    ];
    return colors[(id || 0) % colors.length];
  }, []);

  // Create options array with enriched personas and "add new" option
  const personaDropdownOptions = useMemo<PersonaDropdownOption[]>(() => {
    const addNewOption: AddNewPersonaOption = {
      isAddNew: true,
      id: 'add-new-persona'
    };
    return [...enrichedPersonas, addNewOption];
  }, [enrichedPersonas]);

  // Find the enriched selected persona
  const enrichedSelectedPersona = useMemo(() => {
    if (!selectedPersona) return null;
    return enrichedPersonas.find(sp => sp.personaId === selectedPersona.personaId) || selectedPersona;
  }, [enrichedPersonas, selectedPersona]);

  // Handle persona selection or add new action from auto-complete
  const handlePersonaAutocompleteChange = useCallback((selectedOption: PersonaDropdownOption | null) => {
    if (!selectedOption) {
      onPersonaChange(null);
      return;
    }

    // Check if this is the "add new" option
    if ('isAddNew' in selectedOption && selectedOption.isAddNew) {
      onAddNewPersona();
      return;
    }

    // Handle regular persona selection
    const scenarioPersona = selectedOption as ScenarioPersonaDto;
    onPersonaChange(scenarioPersona.personaId);
  }, [onPersonaChange, onAddNewPersona]);

  // Get usage type label for display
  const getUsageTypeLabel = useCallback((usage: PersonaUsage) => {
    switch (usage as unknown as string) {
      case 'Default':
        return translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.addPersonaLabels.default));
      case 'Intro':
        return translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.addPersonaLabels.intro));
      case 'Fallback':
        return translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.addPersonaLabels.fallback));
      default:
        return translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.addPersonaLabels.normal));
    }
  }, [translate]);

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      options={personaDropdownOptions}
      value={enrichedSelectedPersona || null}
      onChange={(event, newValue) => {
        handlePersonaAutocompleteChange(newValue);
      }}
      getOptionLabel={(option) => {
        if ('isAddNew' in option && option.isAddNew) {
          return getUsageTypeLabel(usageType);
        }
        const scenarioPersona = option as ScenarioPersonaDto;
        return (
          scenarioPersona.persona?.name ||
          translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.unnamedPersona))
        );
      }}
      isOptionEqualToValue={(option, value) => {
        // Handle null/undefined values
        if (!option || !value) {
          return false;
        }
        // Never match the "add new" option as selected
        if ('isAddNew' in option || 'isAddNew' in value) {
          return false;
        }
        return option.personaId === value.personaId;
      }}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props;
        
        // Handle "Add New" option
        if ('isAddNew' in option && option.isAddNew) {
          return (
            <Box
              key={key}
              component="li"
              sx={{ 
                '& > span': { mr: 1, flexShrink: 0 },
                borderTop: '1px solid',
                borderTopColor: 'divider',
                mt: 0.5,
                pt: 0.5,
                fontWeight: 'medium',
                color: 'primary.main'
              }}
              {...otherProps}
            >
              <AddIcon sx={{ mr: 1, color: 'primary.main' }} />
              {getUsageTypeLabel(usageType)}
            </Box>
          );
        }
        
        // Handle regular persona options
        const scenarioPersona = option as ScenarioPersonaDto;
        const persona = scenarioPersona.persona;
        const personaIndex = scenarioPersonas.findIndex(sp => sp.personaId === scenarioPersona.personaId);
        const isPersonaLoading = personaIndex >= 0 && personaQueries[personaIndex]?.isLoading;
        const hasPersonaError = personaIndex >= 0 && personaQueries[personaIndex]?.isError;
        
        // Use persona name or fallback to ID if loading failed
        const personaName = persona?.name || 
          (hasPersonaError ? `Persona ${scenarioPersona.personaId}` : '') ||
          translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.unnamedPersona));
        const roleName = getRoleNameById(scenarioPersona.roleId);
          
        const avatarUrl = persona?.avatarUrl ? getAvatarUrlWithSas(persona.avatarUrl) : '';
        
        return (
          <Box
            key={key}
            component="li"
            sx={{ 
              '& > span': { mr: 1, flexShrink: 0 },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              justifyContent: 'space-between',
              width: '100%'
            }}
            {...otherProps}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              {isPersonaLoading ? (
                <Skeleton variant="circular" width={32} height={32} />
              ) : (
                <Avatar
                  src={avatarUrl}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: !avatarUrl ? getAvatarColor(persona?.id || scenarioPersona.personaId) : undefined,
                    fontSize: '0.75rem',
                    opacity: hasPersonaError ? 0.6 : 1
                  }}
                >
                  {!avatarUrl && getInitials(personaName)}
                </Avatar>
              )}
              <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                {isPersonaLoading ? (
                  <>
                    <Skeleton variant="text" width={120} height={20} />
                    <Skeleton variant="text" width={80} height={14} />
                  </>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        variant="body2" 
                        noWrap
                        sx={{ opacity: hasPersonaError ? 0.6 : 1 }}
                      >
                        {personaName}
                      </Typography>
                      {roleName && (
                        <Chip size="small" label={roleName} variant="outlined" />
                      )}
                    </Box>
                    {persona?.description && persona.description.trim() && (
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary',
                          opacity: hasPersonaError ? 0.6 : 1
                        }}
                        noWrap
                      >
                        {persona.description}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </Box>
            
            {/* Edit button */}
            {showEditButton && onEditPersona && persona && !isPersonaLoading && (
              <IconButton
                size="small"
                sx={{ 
                  opacity: 0.7,
                  '&:hover': { opacity: 1 }
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEditPersona(persona);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        );
      }}
      renderInput={(params) => {
        // Get selected persona for avatar display in input
        const selectedPersonaData = enrichedSelectedPersona?.persona;
        const selectedPersonaName = selectedPersonaData?.name || translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.unnamedPersona));
        const selectedAvatarUrl = selectedPersonaData?.avatarUrl ? getAvatarUrlWithSas(selectedPersonaData.avatarUrl) : '';
        const selectedRoleName = getRoleNameById(enrichedSelectedPersona?.roleId);
        
        // Check if the selected persona is loading
        const selectedPersonaIndex = enrichedSelectedPersona ? scenarioPersonas.findIndex(sp => sp.personaId === enrichedSelectedPersona.personaId) : -1;
        const isSelectedPersonaLoading = selectedPersonaIndex >= 0 && personaQueries[selectedPersonaIndex]?.isLoading;

        const hasLanguageMismatch = !!(scenarioLanguage && selectedPersonaData?.languageCode && selectedPersonaData.languageCode !== scenarioLanguage);

        return (
          <Box sx={{ position: 'relative' }}>
            <TextField
              {...params}
              margin="normal"
              variant="outlined"
              label={label}
              error={hasError || hasLanguageMismatch}
              helperText={
                hasError && errorMessage
                  ? errorMessage
                  : isLoadingPersonas 
                  ? translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioPersonasSettings.loadingPersonaDetails))
                  : helperText
              }
              placeholder={placeholder || 'None'}
              InputProps={{
                ...params.InputProps,
                startAdornment: enrichedSelectedPersona ? (
                  <InputAdornment position="start">
                    {isSelectedPersonaLoading ? (
                      <Skeleton variant="circular" width={32} height={32} />
                    ) : (
                      <Avatar
                        src={selectedAvatarUrl}
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: !selectedAvatarUrl ? getAvatarColor(selectedPersonaData?.id) : undefined,
                          fontSize: '0.75rem'
                        }}
                      >
                        {!selectedAvatarUrl && getInitials(selectedPersonaName)}
                      </Avatar>
                    )}
                  </InputAdornment>
                ) : undefined,
                endAdornment: (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {selectedRoleName && (
                      <Chip size="small" label={selectedRoleName} variant="outlined" />
                    )}
                    {params.InputProps.endAdornment}
                  </Box>
                ),
              }}
            />
            {hasLanguageMismatch && (
              <Box sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'warning.light',
                opacity: 0.08,
                pointerEvents: 'none',
                borderRadius: 1
              }} />
            )}
          </Box>
        );
      }}
    />
  );
};
